import React, { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useDirectMessages } from '@/hooks/useSendMessage';
import { useAuthor } from '@/hooks/useAuthor';
import { nip19 } from 'nostr-tools';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Mail, Plus, Search, MessageSquare, Check, ShieldAlert } from 'lucide-react';

export function ChatPage() {
  useSeoMeta({
    title: 'Messages / Twittstr',
    description: 'Private encrypted conversation board fully secured with NIP-17 and NIP-59 envelopes.',
  });

  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const { data: conversations = [], isLoading } = useDirectMessages();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [newChatNpub, setNewChatNpub] = useState('');
  const [npubSearchError, setNewChatNpubError] = useState('');

  const handleStartConvo = () => {
    try {
      setNewChatNpubError('');
      if (!newChatNpub.trim()) return;

      const decoded = nip19.decode(newChatNpub.trim());
      if (decoded.type !== 'npub' && decoded.type !== 'nprofile') {
        setNewChatNpubError('Invalid format. Please supply a valid npub1... key.');
        return;
      }

      setIsSearchOpen(false);
      setNewChatNpub('');
      navigate(`/chat/${newChatNpub.trim()}`);
    } catch {
      setNewChatNpubError('Decryption failed. Confirm the npub values resemble standard bech32.');
    }
  };

  return (
    <div className="flex flex-col w-full min-h-screen bg-background text-foreground">
      {/* Messages Header */}
      <header className="sticky top-0 bg-background/85 backdrop-blur-md z-30 border-b border-border p-4 flex items-center justify-between">
        <span className="font-extrabold text-xl">Messages</span>
        {user && (
          <Button 
            onClick={() => setIsSearchOpen(true)}
            size="icon" 
            className="rounded-full size-10 bg-sky-500 hover:bg-sky-600 text-white cursor-pointer"
          >
            <Plus className="size-5" />
          </Button>
        )}
      </header>

      {/* Main Panel Content */}
      <div className="flex flex-col divide-y divide-border flex-1">
        {!user ? (
          // Locked out prompt
          <div className="p-8 text-center flex flex-col items-center justify-center gap-3 flex-1">
            <Card className="border-dashed bg-secondary/15 w-full max-w-sm rounded-2xl py-8">
              <CardContent className="flex flex-col items-center justify-center text-center gap-3 p-4">
                <Mail className="size-8 text-sky-500" />
                <p className="font-extrabold text-[15px]">Sign in to start chatting</p>
                <p className="text-xs text-muted-foreground leading-normal">
                  Private direct-messages conform to NIP-17 protocols. Sign in with extension or local keypairs to decipher threads.
                </p>
              </CardContent>
            </Card>
          </div>
        ) : isLoading ? (
          // Loading Skeletons
          Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="p-4 flex gap-3 border-b border-border items-center animate-pulse">
              <Skeleton className="size-11 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/4 rounded-md" />
                <Skeleton className="h-4 w-3/4 rounded-md" />
              </div>
            </div>
          ))
        ) : conversations.length > 0 ? (
          // Conversations list
          conversations.map((convo) => (
            <ConversationRow key={convo.peerPubkey} peerPubkey={convo.peerPubkey} lastMsg={convo.lastMessage} />
          ))
        ) : (
          // Empty State
          <div className="p-8 text-center flex flex-col items-center justify-center gap-3 my-12">
            <MessageSquare className="size-12 text-muted-foreground" />
            <p className="font-extrabold text-lg mt-2">Welcome to your inbox!</p>
            <p className="text-sm text-muted-foreground max-w-xs leading-normal">
              Drop premium encrypted envelopes to any other Nostr user secretly using their npub handles.
            </p>
            <Button 
              onClick={() => setIsSearchOpen(true)}
              className="rounded-full bg-sky-500 hover:bg-sky-600 font-bold px-6 h-10 mt-2 cursor-pointer"
            >
              Start conversation
            </Button>
          </div>
        )}
      </div>

      {/* Start Chat Dialog */}
      <Dialog open={isSearchOpen} onOpenChange={setIsSearchOpen}>
        <DialogContent className="sm:max-w-[420px] rounded-2xl p-4 border border-border">
          <DialogTitle className="font-extrabold text-[17px] mb-1">New Message</DialogTitle>
          <div className="flex flex-col gap-3 mt-2">
            <span className="text-xs text-muted-foreground">Search by user keys (npub1...) to begin private conversation pipeline.</span>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 class size-4 text-muted-foreground" />
              <Input 
                placeholder="npub1..." 
                className="pl-9 h-11 rounded-full bg-secondary/30 focus-visible:bg-background"
                value={newChatNpub}
                onChange={(e) => setNewChatNpub(e.target.value)}
              />
            </div>
            {npubSearchError && (
              <span className="text-xs text-rose-500 font-medium">{npubSearchError}</span>
            )}
            <Button 
              onClick={handleStartConvo}
              disabled={!newChatNpub.trim()}
              className="w-full h-11 bg-sky-500 hover:bg-sky-600 text-white font-bold rounded-full cursor-pointer"
            >
              Start Chat
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Conversation rendering row
function ConversationRow({ peerPubkey, lastMsg }: { peerPubkey: string; lastMsg: any }) {
  const author = useAuthor(peerPubkey);
  const metadata = author.data?.metadata;
  const navigate = useNavigate();

  const peerNpub = nip19.npubEncode(peerPubkey);

  return (
    <div 
      onClick={() => navigate(`/chat/${peerNpub}`)}
      className="p-4 flex gap-3 hover:bg-secondary/10 transition-colors border-b border-border items-center cursor-pointer select-none"
    >
      <img 
        src={metadata?.picture || `https://robohash.org/${peerPubkey}.png?set=set4`}
        alt="Author Avatar" 
        className="size-11 rounded-full object-cover border border-border shrink-0 bg-secondary"
      />
      <div className="flex-1 min-w-0 pr-1 flex flex-col gap-0.5 leading-tight">
        <div className="flex items-center justify-between">
          <span className="font-bold truncate text-[15px]">{metadata?.display_name || metadata?.name || 'Anonymous'}</span>
          <span className="text-xs text-muted-foreground shrink-0 leading-none">
            {new Date(lastMsg.created_at * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </span>
        </div>
        <p className="text-[13.5px] text-muted-foreground truncate leading-normal mt-0.5 max-w-[420px]">
          {lastMsg.content}
        </p>
      </div>
    </div>
  );
}
export default ChatPage;
